import type { TemplateField, TemplateSchema } from '@/api';
import type { TemplateFieldMetadataMap } from '@/types/contractGeneration/wizard';
import {
  childMetadataPath,
  lookupFieldMetadata,
  resolveFieldLabel,
} from '@/utils/contractGeneration/templateFieldLabels';

/**
 * A still-unanswered required field, in the words the wizard uses for it.
 *
 * The validator reports what is missing as runtime paths — `client.name`,
 * `services[1].fee` — which is the right thing to address a field by and the
 * wrong thing to show a person. This turns one into the other, so "Generate"
 * can say what it is waiting for instead of only refusing.
 */
export interface MissingField {
  /** Runtime path, unchanged — what the answer panel is asked to jump to. */
  path: string;
  /** The field itself: "Monthly Fee". */
  label: string;
  /** Everything it hangs off, already joined: "Services · Item 2". */
  context: string;
}

interface PathSegment {
  key: string;
  /** Set when the segment addresses one row of a list: `services[1]` → 1. */
  index: number | null;
}

/**
 * Split a runtime path into its segments. Keys are Jinja identifiers, so the
 * only brackets a path can carry are list indices.
 */
function parsePath(path: string): PathSegment[] {
  return path.split('.').map(raw => {
    const match = /^(.*?)\[(\d+)\]$/.exec(raw);
    return match ? { key: match[1], index: Number(match[2]) } : { key: raw, index: null };
  });
}

/**
 * Name one missing field the way its section and inputs do: authored labels
 * where an author wrote them, humanized keys otherwise, and list rows counted
 * from 1 the way the panel numbers them.
 */
function describeField(
  path: string,
  schema: TemplateSchema,
  metadata: TemplateFieldMetadataMap | null
): MissingField {
  const segments = parsePath(path);
  const labels: string[] = [];
  // Typed as possibly-missing on purpose: a path can outlive the schema it was
  // recorded against (a template edited under a draft), and the walk stops at
  // the first segment the schema no longer has.
  let fields: Record<string, TemplateField | undefined> = schema.fields ?? {};
  let metaPath = '';

  for (const segment of segments) {
    const field = fields[segment.key];
    metaPath = childMetadataPath(metaPath, segment.key);
    const label = resolveFieldLabel(lookupFieldMetadata(metadata, metaPath), segment.key);
    // A list row reads as "Services 2", not "Services" — with several rows open
    // the label alone would point at all of them.
    labels.push(segment.index === null ? label : `${label} ${segment.index + 1}`);

    if (!field) break;
    if (segment.index === null) {
      fields = field.fields ?? {};
    } else {
      fields = field.item_fields ?? {};
      metaPath = `${metaPath}[]`;
    }
  }

  return {
    path,
    label: labels.at(-1) ?? path,
    context: labels.slice(0, -1).join(' · '),
  };
}

/**
 * Every missing answer, named. Order follows the validator, which walks the
 * schema in document order — so the list reads top-to-bottom the way the
 * sections do.
 */
export function describeMissingFields(
  paths: string[],
  schema: TemplateSchema | null,
  metadata: TemplateFieldMetadataMap | null
): MissingField[] {
  if (!schema) return [];
  return paths.map(path => describeField(path, schema, metadata));
}
